import React from "react";
import { useFetch } from "../../../lib/useFetch";
import { LoadingSpinner } from "../../../lib/Spinner";
import {
  filterToGRPJAPIUrl,
  sortOrderJAPI,
  sortFieldJAPI,
  excludeFieldJAPI,
} from "../../search/api/grpQueryCreatorJAPI";
import { useSelector } from "react-redux";
import { ErrorMsg } from "../../../lib/ErrorMsg";
import { PAGE_SIZE, PUB_LANG } from "../../../redux/slices/api/initialisePreferences";
import { sourceFromQueryString, getEsIndex, createReportUrl } from "./urls/publicationUrls";
import { timeAgo } from "lib/timeAgo";
import { i18n } from "app/i18n/i18n";
import { toUTCDate } from "lib/dateUtils";
import { getKeywordOrTitle } from "lib/routing";
import { useProdCopyUrl } from "pages/redesign/preferences/redesignUtils";

const jsApiUrl = (prodCopyUrl) => `/research/${prodCopyUrl}/pub/search/filter${getEsIndexParam()}`;

// This class needs refactoring since JAPI integration was added
export function JAPIPublicationLoader({
  render,
  showSpinner = true,
  scrollToTop = true,
  aggregations = {},
  defaultFilters,
}) {
  const prodCopyUrl = useProdCopyUrl();
  const body = useCreateQueryBody(aggregations, defaultFilters);
  const [data, isLoading, error] = useFetch(jsApiUrl(prodCopyUrl), JSON.stringify(body));

  if (scrollToTop) window.scrollTo(0, 0);
  if (isLoading && showSpinner) return <LoadingSpinner />;
  if (error) return <ErrorMsg error={error} />;

  data.TotalRecords =
    data && data.hits && data.hits.total ? (isNaN(data.hits.total) ? data.hits.total.value : data.hits.total) : null;

  const queryResults = findQueryResults(data);
  const hits = formatResponse(queryResults);
  const aggregationsList = data && data.aggregations ? parseAggregations(data.aggregations) : {};
  return render(hits, aggregationsList, data);
}

const useCreateQueryBody = (aggregations, defaultFilters) => {
  const { sort, filter, pageFrom } = useSelector((state) => state.filter);
  const pageSize = useSelector((state) => state.preferences[PAGE_SIZE][0]);
  const pubLang = useSelector((state) => state.preferences[PUB_LANG]);
  const recordsFrom = pageFrom && pageFrom.From ? (pageFrom.From - 1) * pageSize : 0;
  const excludeFilters = excludeFieldJAPI(filter);

  const { query, filters } = filterToGRPJAPIUrl(defaultFilters || filter, pubLang);
  const source = sourceFromQueryString();

  const body = {
    query,
    filters,
    source,
    from: recordsFrom,
    size: pageSize,
  };

  if (sortFieldJAPI(sort)) {
    body.sortField = sortFieldJAPI(sort);
    body.sortOrder = sortOrderJAPI(sort);
  }

  if (excludeFilters.length) {
    body.excludeFilters = excludeFilters;
  }

  if (aggregations) {
    body.aggregations = aggregations;
  }

  return body;
};

export const parseAggregations = (aggregations) =>
  Object.entries(aggregations).reduce((acc, [key, value]) => {
    acc[key.replace("sterms#", "")] = value.buckets.map((option) => option.key).filter((item) => !!item);
    return acc;
  }, {});

// to refactor into format response
const findQueryResults = (data, lang = i18n.getLanguage(), _isExactSearch = isExactSearch()) =>
  data && data.hits
    ? data.hits.hits.map((q) => {
        if (q.highlight) {
          q._source.Highlight = {};
          q._source.Highlight.content = q.highlight["content.body"] || null;
          q._source.Highlight.title = q.highlight["title"] || null;
          q._source.Highlight.colour = lang === "en" || _isExactSearch ? "highlight-text-yellow" : "none";
        }
        return q._source;
      })
    : [];

export const formatResponse = (hits, lang = i18n.getLanguage()) =>
  hits.map((el) => {
    el.publicationDateLocale = timeAgo(toUTCDate(el.publicationDate), lang);

    el.PageCount = el.pageCount;
    delete el.pageCount;

    const appName = "GRPUI";
    const cid = "";

    el.commonEndpoint = createReportUrl(el.id, "", appName, cid); // main publication link
    el.pdfUrl = createReportUrl(el.id, ".file", appName, cid); // download as PDF
    el.htmlUrl = existsHtmlFormat(el) ? createReportUrl(el.id, ".html", appName, cid) : null; //used only for labelling
    el.podcastUrl = isPodcastUrl(el) ? createReportUrl(el.id, "", appName, cid) : null;
    el.pubUrl = el.podcastUrl || el.commonEndpoint;

    return el;
  });

export const existsHtmlFormat = ({ resources = [] }) =>
  resources.filter((res) => res.isCDNReady && res.format === "html5").length > 0;

export const existsExternal = ({ resources = [] }) =>
  resources.filter((res) => res.isCDNReady && res.format === "external").length > 0;

export const isPodcastUrl = ({ resources = [] }) =>
  resources.filter((res) => res.isCDNReady && (res.extension === ".mp3" || res.extension === ".mp4")).length > 0;
export const isPDFReport = ({ resources = [] }) =>
  resources.filter((res) => res.extension && res.extension === ".pdf").length > 0;
const getEsIndexParam = () => (getEsIndex() ? "?es-index=" + getEsIndex() : "");

const isExactSearch = () => getKeywordOrTitle()?.includes('"');
